// Pure helpers for a campaign's extra images: the When presets, the words for
// a window and an audience, slots, A/B arm letters and the timeline segments.
// No React and no requests. Only type imports from the API module, because a
// real import of it pulls in axios.
import type {
  CampaignMedia, MediaAudience, MediaSpan, MediaUpdate, MediaVariant,
} from "../../../api/maintenance/campaigns";

const IST_OFFSET_MS = 330 * 60000; // Asia/Kolkata, the program timezone. No DST.
const DAY_MS = 86400000;

/** Server timestamps are naive UTC. One that already carries an offset is read as it is. */
export const parseTs = (s: string | null | undefined): number =>
  s ? new Date(/(Z|[+-]\d\d:?\d\d)$/.test(s) ? s : s + "Z").getTime() : NaN;

/** UTC with a Z and no milliseconds: what the builder already sends for starts_at. */
export const toIso = (ms: number): string => new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");

/** 00:00 IST on the IST day that holds `ms`. */
export const istDayStart = (ms: number): number =>
  Math.floor((ms + IST_OFFSET_MS) / DAY_MS) * DAY_MS - IST_OFFSET_MS;

/** 00:00 IST on the campaign's last day. A campaign ending at 00:00 IST has
 *  its last day the day before, not a final day with no time in it. */
const lastDayStart = (endsAt: string): number => istDayStart(parseTs(endsAt) - 1);

export interface CampaignDates { starts_at: string; ends_at: string; }
export interface MediaWindow { show_from: string | null; show_until: string | null; }
export type WhenPreset = "whole" | "before" | "last" | "custom";

/** Both dates set and in order. Without them "Before it starts" and "Last N days" mean nothing. */
export const datesOk = (c: CampaignDates): boolean => {
  const s = parseTs(c.starts_at), e = parseTs(c.ends_at);
  return isFinite(s) && isFinite(e) && e > s;
};

/** IST days the campaign touches: the most "Last N days" can be. */
export const campaignDays = (c: CampaignDates): number =>
  Math.round((lastDayStart(c.ends_at) - istDayStart(parseTs(c.starts_at))) / DAY_MS) + 1;

/** "Last N days" starts at 00:00 IST on the day N-1 days before the last day:
 *  ends Sun 15 Nov 23:59 IST, N = 3 -> from Fri 13 Nov 00:00 IST. */
export const lastDaysFrom = (c: CampaignDates, n: number): string =>
  toIso(lastDayStart(c.ends_at) - (n - 1) * DAY_MS);

/** The window a preset stands for, from the campaign's own dates. */
export const presetWindow = (p: Exclude<WhenPreset, "custom">, c: CampaignDates, n = 3): MediaWindow => {
  if (p === "before") return { show_from: null, show_until: toIso(parseTs(c.starts_at)) };
  if (p === "last") return { show_from: lastDaysFrom(c, n), show_until: null };
  return { show_from: null, show_until: null };
};

/** A stored window read back as the preset that produces it. Anything else is custom. */
export const readPreset = (w: MediaWindow, c: CampaignDates): { preset: WhenPreset; n?: number } => {
  if (!w.show_from && !w.show_until) return { preset: "whole" };
  if (!datesOk(c)) return { preset: "custom" };
  if (!w.show_from && parseTs(w.show_until) === parseTs(c.starts_at)) return { preset: "before" };
  if (w.show_from && !w.show_until) {
    const from = parseTs(w.show_from);
    const n = Math.round((lastDayStart(c.ends_at) - from) / DAY_MS) + 1;
    if (n >= 1 && parseTs(lastDaysFrom(c, n)) === from) return { preset: "last", n };
  }
  return { preset: "custom" };
};

const pad = (n: number) => String(n).padStart(2, "0");

/** A datetime-local value, read in the viewer's own timezone. */
export const localInputToMs = (v: string): number => {
  const m = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d)/.exec(v);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : NaN;
};
export const msToLocalInput = (ms: number): string => {
  if (!isFinite(ms)) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** The When fields of the image form. `from`/`until` are datetime-local values in the viewer's time. */
export interface WhenInput { when: WhenPreset; days: string; from: string; until: string; }

export const windowFor = (d: WhenInput, c: CampaignDates): { window: MediaWindow } | { error: string } => {
  if (d.when === "whole") return { window: presetWindow("whole", c) };
  if (d.when === "custom") {
    const f = d.from ? localInputToMs(d.from) : null;
    const u = d.until ? localInputToMs(d.until) : null;
    if ((f != null && !isFinite(f)) || (u != null && !isFinite(u))) return { error: "Enter a full date and time." };
    if (f != null && u != null && u <= f) return { error: "Until has to be after From." };
    return { window: { show_from: f == null ? null : toIso(f), show_until: u == null ? null : toIso(u) } };
  }
  if (!datesOk(c)) return { error: "Set the campaign's start and end first." };
  if (d.when === "before") return { window: presetWindow("before", c) };
  const n = Number(d.days), max = campaignDays(c);
  if (!Number.isInteger(n) || n < 1) return { error: "Days has to be a whole number, 1 or more." };
  if (n > max) return { error: `The campaign runs ${max} day${max === 1 ? "" : "s"}.` };
  return { window: presetWindow("last", c, n) };
};

/** In the order the server breaks ties: an image for an audience earlier in
 *  the list beats one for a later audience, and any of them beats Everyone. */
export const WHO: [MediaAudience | null, string][] = [
  [null, "Everyone"],
  ["earned_here", "Earned in this campaign"],
  ["near_miss", "Topped up just under the minimum"],
  ["dormant", "No top-up in 60 days"],
  ["holding_promo", "Has unspent cashback"],
  ["benefited", "Earned cashback before"],
];
export const whoLabel = (a: MediaAudience | null): string => {
  const hit = WHO.find(([k]) => k === a);
  return hit ? hit[1] : String(a);
};
/** Everyone first, then the server's precedence; anything unknown last. */
export const whoRank = (a: MediaAudience | null): number => {
  const i = WHO.findIndex(([k]) => k === a);
  return i < 0 ? WHO.length : i;
};

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const hm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** "Fri 13 Nov, 00:00" in the viewer's own time. */
export const dayTime = (ms: number): string => {
  const d = new Date(ms);
  return `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}, ${hm(d)}`;
};

/** "6–12 Nov" in the viewer's own time. `until` is exclusive. */
export const shortRange = (from: number, until: number): string => {
  const a = new Date(from), b = new Date(until - 1);
  if (a.toDateString() === b.toDateString()) return `${a.getDate()} ${MON[a.getMonth()]}, ${hm(a)}–${hm(new Date(until))}`;
  if (a.getFullYear() !== b.getFullYear())
    return `${a.getDate()} ${MON[a.getMonth()]} ${a.getFullYear()} – ${b.getDate()} ${MON[b.getMonth()]} ${b.getFullYear()}`;
  if (a.getMonth() !== b.getMonth()) return `${a.getDate()} ${MON[a.getMonth()]} – ${b.getDate()} ${MON[b.getMonth()]}`;
  return `${a.getDate()}–${b.getDate()} ${MON[b.getMonth()]}`;
};

/** A window in words, against the campaign's dates. */
export const whenLabel = (w: MediaWindow, c: CampaignDates): string => {
  const p = readPreset(w, c);
  if (p.preset === "whole") return "Whole campaign";
  if (p.preset === "before") return "Before it starts";
  if (p.preset === "last") return `Last ${p.n} day${p.n === 1 ? "" : "s"}, from ${dayTime(parseTs(w.show_from))}`;
  if (w.show_from && w.show_until) {
    const f = new Date(parseTs(w.show_from)), u = new Date(parseTs(w.show_until));
    return f.toDateString() === u.toDateString()
      ? `${dayTime(f.getTime())}–${hm(u)}`
      : `${dayTime(f.getTime())} → ${dayTime(u.getTime())}`;
  }
  return w.show_from ? `From ${dayTime(parseTs(w.show_from))}` : `Until ${dayTime(parseTs(w.show_until))}`;
};

export const mediaName = (m: Pick<CampaignMedia, "id" | "label">): string => m.label?.trim() || `Image ${m.id}`;

type SlotFields = Pick<CampaignMedia, "audience" | "show_from" | "show_until">;

/** Images share a slot when they are for the same people over the same window.
 *  Compared as instants, so a naive and a Z timestamp of one moment match. */
export const slotKey = (m: SlotFields): string =>
  `${m.audience ?? ""}|${m.show_from ? parseTs(m.show_from) : ""}|${m.show_until ? parseTs(m.show_until) : ""}`;

export const sameSlot = <T extends SlotFields & { id: number }>(items: T[], s: SlotFields, exceptId?: number | null): T[] =>
  items.filter((m) => m.id !== exceptId && slotKey(m) === slotKey(s));

export const LETTERS: MediaVariant[] = ["A", "B", "C", "D"];
const letterRank = (v: MediaVariant | null | undefined) => (v ? LETTERS.indexOf(v) : -1);

export interface ArmPlan {
  /** the letter the joining image takes; null when A–D are all taken */
  variant: MediaVariant | null;
  /** images already in the slot that need a letter first */
  relabel: { id: number; variant: MediaVariant }[];
}

/** An image joining the A/B test in a slot. `slot` is the images already
 *  there. Any of them without a letter gets one first, oldest first, so a
 *  lone image becomes A; the joiner then takes the next free letter. `keep` is
 *  a letter the joiner already holds, kept while nobody else in the slot has it. */
export const planArm = (slot: Pick<CampaignMedia, "id" | "variant">[], keep: MediaVariant | null = null): ArmPlan => {
  const used = new Set<MediaVariant>();
  slot.forEach((m) => { if (m.variant) used.add(m.variant); });
  const mine = keep && !used.has(keep) ? keep : null;
  if (mine) used.add(mine);
  const relabel: ArmPlan["relabel"] = [];
  for (const m of slot.filter((x) => !x.variant).sort((a, b) => a.id - b.id)) {
    const v = LETTERS.find((l) => !used.has(l));
    if (!v) return { variant: null, relabel: [] };
    used.add(v);
    relabel.push({ id: m.id, variant: v });
  }
  const variant = mine ?? LETTERS.find((l) => !used.has(l)) ?? null;
  return variant ? { variant, relabel } : { variant: null, relabel: [] };
};

export interface MediaFields extends MediaWindow {
  label: string | null;
  audience: MediaAudience | null;
  variant: MediaVariant | null;
}

/** PATCH sets what it is given and nulls what `clear` names. */
export const patchBody = (f: MediaFields): MediaUpdate => {
  const body: MediaUpdate = { clear: [] };
  if (f.label != null) body.label = f.label; else body.clear!.push("label");
  if (f.show_from != null) body.show_from = f.show_from; else body.clear!.push("show_from");
  if (f.show_until != null) body.show_until = f.show_until; else body.clear!.push("show_until");
  if (f.audience != null) body.audience = f.audience; else body.clear!.push("audience");
  if (f.variant != null) body.variant = f.variant; else body.clear!.push("variant");
  return body;
};

const cmp = (x: number, y: number) => (x < y ? -1 : x > y ? 1 : 0);

/** List order: by audience, then window, then arm, so a test's arms sit together. */
export const byPlace = (a: CampaignMedia, b: CampaignMedia): number =>
  whoRank(a.audience) - whoRank(b.audience)
  || cmp(a.show_from ? parseTs(a.show_from) : -Infinity, b.show_from ? parseTs(b.show_from) : -Infinity)
  || cmp(a.show_until ? parseTs(a.show_until) : Infinity, b.show_until ? parseTs(b.show_until) : Infinity)
  || letterRank(a.variant) - letterRank(b.variant)
  || a.id - b.id;

export interface Segment { from: number; until: number; ids: (number | null)[]; }

/** Timeline spans in order. Spans over the same stretch fold into one
 *  segment, and a segment showing one arm of an A/B test shows every arm. */
export const segmentsOf = (spans: MediaSpan[], items: CampaignMedia[]): Segment[] => {
  const out: Segment[] = [];
  spans
    .map((s, i) => ({ s, i, from: parseTs(s.from), until: parseTs(s.until) }))
    .sort((a, b) => cmp(a.from, b.from) || a.i - b.i)
    .forEach(({ s, from, until }) => {
      const last = out[out.length - 1];
      if (last && last.from === from && last.until === until) {
        if (!last.ids.includes(s.media_id)) last.ids.push(s.media_id);
      } else out.push({ from, until, ids: [s.media_id] });
    });
  const find = (id: number | null) => (id == null ? undefined : items.find((x) => x.id === id));
  out.forEach((seg) => {
    seg.ids.slice().forEach((id) => {
      const m = find(id);
      if (!m || !m.variant) return;
      sameSlot(items, m, m.id).forEach((x) => { if (x.variant && !seg.ids.includes(x.id)) seg.ids.push(x.id); });
    });
    seg.ids.sort((a, b) => letterRank(find(a)?.variant) - letterRank(find(b)?.variant));
  });
  return out;
};

/** The viewer's timezone as a short name ("IST" in India), to label inputs typed in it. */
export const localZone = (): string => {
  try {
    const p = new Intl.DateTimeFormat("en-IN", { timeZoneName: "short" }).formatToParts(new Date())
      .find((x) => x.type === "timeZoneName");
    return p?.value || "local time";
  } catch {
    return "local time";
  }
};
